import { useEffect, useState } from "react";
import { Users, Pencil, Trash2, Check, X, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { EMOTIONS } from "@/components/DialogueComposer";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Remembered characters: rename, change default emotion, or forget.
export default function CharactersPanel({ refreshKey = 0 }) {
  const [chars, setChars] = useState([]);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");

  const [fx, setFx] = useState({});
  const load = () => api.get("/voice/characters").then(({ data }) => setChars(data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);
  useEffect(() => { api.get("/voice/runtime/effects").then(({ data }) => setFx(data)).catch(() => {}); }, []);

  const save = async (ch, patch) => {
    try {
      await api.put(`/voice/characters/${encodeURIComponent(ch.name)}`, patch);
      toast.success(`Personnage « ${patch.display || ch.display} » mis à jour.`);
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Mise à jour impossible."); }
  };

  const forget = async (ch) => {
    try {
      await api.delete(`/voice/characters/${encodeURIComponent(ch.name)}`);
      toast.success(`« ${ch.display} » oublié.`);
      load();
    } catch { toast.error("Suppression impossible."); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="characters-panel">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display font-bold flex items-center gap-2"><Users size={16} className="text-primary" /> Personnages</h3>
        <span className="text-[10px] font-mono text-muted-foreground" data-testid="characters-count">{chars.length}</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">Voix et émotion mémorisées à chaque dialogue généré — réutilisées automatiquement dans vos prochaines scènes.</p>
      {chars.length === 0 ? (
        <div className="text-xs text-muted-foreground/70" data-testid="characters-empty">Aucun personnage mémorisé pour l'instant.</div>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {chars.map((ch) => (
            <div key={ch.name} className="rounded-xl border border-border bg-background px-3 py-2 text-xs" data-testid={`character-${ch.name.replace(/\s+/g, "-")}`}>
              {editing === ch.name ? (
                <div className="flex items-center gap-1.5">
                  <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} className="h-7 text-xs bg-card border-border" data-testid="character-rename-input" onKeyDown={(e) => e.key === "Enter" && save(ch, { display: name.trim() })} />
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-primary" onClick={() => save(ch, { display: name.trim() })} data-testid="character-rename-save"><Check size={14} /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(null)}><X size={14} /></Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <UserCheck size={13} className="text-primary shrink-0" />
                  <span className="font-semibold truncate flex-1" data-testid="character-name">{ch.display}</span>
                  <span className="font-mono text-muted-foreground truncate max-w-[110px]" title={ch.voice}>{ch.voice}{ch.provider === "elevenlabs" ? " · 11L" : ""}</span>
                  <Select value={ch.emotion || "neutre"} onValueChange={(v) => save(ch, { emotion: v })}>
                    <SelectTrigger className="h-7 w-24 text-[11px] bg-card border-border" data-testid="character-emotion"><SelectValue /></SelectTrigger>
                    <SelectContent className="glass border-border">{EMOTIONS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => { setEditing(ch.name); setName(ch.display); }} title="Renommer" data-testid="character-rename"><Pencil size={13} /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => forget(ch)} title="Oublier" data-testid="character-forget"><Trash2 size={13} /></Button>
                </div>
              )}
              {editing !== ch.name && (
                <div className="flex items-center gap-1.5 mt-1.5 pl-5" data-testid={`character-fx-row-${ch.name.replace(/\s+/g, "-")}`}>
                  <Select value={ch.effect || "none"} onValueChange={(v) => save(ch, { effect: v })}>
                    <SelectTrigger className="h-6 w-28 text-[10px] bg-card border-border" data-testid="character-effect"><SelectValue /></SelectTrigger>
                    <SelectContent className="glass border-border">{Object.entries(fx.effects || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={ch.culture || "none"} onValueChange={(v) => save(ch, { culture: v })}>
                    <SelectTrigger className="h-6 w-28 text-[10px] bg-card border-border" data-testid="character-culture"><SelectValue /></SelectTrigger>
                    <SelectContent className="glass border-border">{Object.keys(fx.cultures || {}).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                  </Select>
                  <span className="text-[10px] text-muted-foreground">effet · culture (SDK Speak)</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
